import React, { useCallback, useEffect, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Drawer from '@atlaskit/drawer';
import Lozenge from '@atlaskit/lozenge';
import ModalDialog, {
  ModalTransition,
  ModalTitle,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import * as colors from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Onboarding as PipelinesOnboarding } from 'src/components/pipelines/components/onboarding';
import RunPipelineModal from 'src/components/pipelines/components/run-pipeline';
import {
  ALLOWANCE_LIMIT,
  PipelinesWebsocketEvent,
} from 'src/components/pipelines/constants';
import { Allowance, EditorEnum } from 'src/components/pipelines/models';
import { Editor } from 'src/components/pipelines/pages/pipelines/editor';
import OnboardingCommit from 'src/components/pipelines/pages/pipelines/onboarding-commit';
import {
  clearBuildConfiguration,
  requestBuildConfiguration,
  requestEnablePipelines,
  requestOfficialTemplates,
  requestPipelines,
  requestRepositoryFiles,
  requestRepositoryUserIsMember,
  setValidator,
  verifyPipelinesInstallation,
  webSocketSubscribe,
  webSocketUnsubscribe,
} from 'src/components/pipelines/redux/actions/pipelines';
import {
  getAccount,
  getBuildConfiguration,
  getCapabilities,
  getIsLoadingPipelines,
  getRepository,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { loadAllowanceResource } from 'src/components/pipelines/resources/allowance';
import { createPipeline } from 'src/components/pipelines/utils/create-pipeline';
import queryBranches from 'src/components/pipelines/utils/query-branches';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useHistory__UNSAFE, useParams } from 'src/router/hooks';
import { LoadingPage } from 'src/sections/global/components/loading-page';
import { publishTrackEvent } from 'src/utils/analytics/publish';

const OnboardingWrapper = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  max-width: calc(100vw - 200px);
  min-width: 1000px;
  justify-content: space-between;
  margin: 8px auto 16px;
`;

const EditorContainer = styled.div`
  display: flex;
  max-width: calc(100vw - 200px);
  min-width: 1000px;
  margin: 0 auto;
  flex-wrap: wrap;
  & > div {
    width: 100%;
  }
`;

const EditorHeader = styled.header`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  background: ${token('color.background.input', colors.N20)};
  border: 1px solid ${token('color.border', colors.N40)};
  border-bottom: 0;
  border-radius: 3px 3px 0 0;
  padding: 0 16px;
  strong {
    margin-right: 12px;
  }
`;

const EditorWrapper = styled.div`
  border: 1px solid ${token('color.border', colors.N40)};
`;

type Props = {};

const source = 'pipelinesGettingStartedScreen';

/* eslint @typescript-eslint/ban-types: "warn" */
const Onboarding: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const history = useHistory__UNSAFE();
  const { repositoryFullSlug } = useParams<{
    repositoryFullSlug: string;
  }>();
  const workspaceSlug = repositoryFullSlug.split('/')[0];
  const account = useSelector(getAccount);
  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const buildConfiguration = useSelector(getBuildConfiguration);
  const isLoadingPipelines = useSelector(getIsLoadingPipelines);
  const isBitbucketPipelinesYmlSupportEnabled = useStatsigGate(
    StatsigFeatureKeys.isBitbucketPipelinesYmlSupportEnabled
  ) as boolean;
  const { data: allowanceData, loading: isAllowanceLoading } = useResource(
    loadAllowanceResource
  );

  const defaultAllowance = new Allowance();
  const allowance = allowanceData ?? defaultAllowance;

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isRunPipelineModalOpen, setIsRunPipelineModalOpen] = useState(false);

  const isGetRepositoryAccessLevelEnabled = useStatsigGate(
    StatsigFeatureKeys.isGetRepositoryAccessLevelEnabled
  );

  useEffect(() => {
    dispatch(verifyPipelinesInstallation());
    if (!isGetRepositoryAccessLevelEnabled) {
      dispatch(requestRepositoryUserIsMember('admin'));
    }
    dispatch(requestPipelines({ pagelen: 1 }));
    dispatch(requestRepositoryFiles(repository.mainbranch));
    dispatch(setValidator({ currentEditor: EditorEnum.PipelinesOnboarding }));
    dispatch(requestOfficialTemplates());
    dispatch(
      requestBuildConfiguration('', isBitbucketPipelinesYmlSupportEnabled)
    );
    dispatch(webSocketSubscribe(PipelinesWebsocketEvent.PipelineUpdated));
    return () => {
      dispatch(webSocketUnsubscribe(PipelinesWebsocketEvent.PipelineUpdated));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditor = useCallback(
    code => {
      setIsEditorOpen(true);
      dispatch(setValidator({ code }));
    },
    [dispatch, setIsEditorOpen]
  );

  const closeEditor = useCallback(() => {
    setIsEditorOpen(false);
    setIsDiscardDialogOpen(false);
  }, [setIsEditorOpen]);

  const openDiscardDialog = useCallback(() => {
    setIsDiscardDialogOpen(true);
  }, [setIsDiscardDialogOpen]);

  const closeDiscardDialog = useCallback(() => {
    setIsDiscardDialogOpen(false);
  }, [setIsDiscardDialogOpen]);

  const closeRunPipelineModal = useCallback(
    () => setIsRunPipelineModalOpen(false),
    [setIsRunPipelineModalOpen]
  );

  const openPipelinePage = useCallback(
    (buildNumber: number) => {
      history.push({
        pathname: `/${repositoryFullSlug}/pipelines/results/${buildNumber}`,
        search: location.search,
      });
      setIsRunPipelineModalOpen(false);
    },
    [history, repositoryFullSlug, setIsRunPipelineModalOpen]
  );

  const fetchCreatePipeline = useCallback(
    data => createPipeline(repository, data),
    [repository]
  );

  const fetchBranches = useCallback(
    data => queryBranches(repository, data.name),
    [repository]
  );

  const enablePipelines = useCallback(() => {
    dispatch(clearBuildConfiguration());
    setIsRunPipelineModalOpen(true);
    dispatch(requestEnablePipelines());
    if (!isGetRepositoryAccessLevelEnabled) {
      dispatch(requestRepositoryUserIsMember('contributor'));
    }
    publishTrackEvent({
      actionSubjectId: '',
      actionSubject: 'builds',
      action: 'enabled',
      source,
      attributes: {
        repoUuid: repository.uuid,
        enforce2Fa: !allowance?.isTrusted,
      },
    });
  }, [
    allowance?.isTrusted,
    dispatch,
    isGetRepositoryAccessLevelEnabled,
    repository.uuid,
  ]);

  if (isLoadingPipelines || isAllowanceLoading) {
    return <LoadingPage />;
  }

  return (
    <ContextualAnalyticsData
      sourceName="pipelinesGettingStarted"
      sourceType={SCREEN}
    >
      <FireScreenAnalytics attributes={{ repositoryUuid: repository.uuid }} />
      <OnboardingWrapper data-testid="onboarding">
        <PipelinesOnboarding
          account={account}
          allowance={allowance}
          buildConfiguration={buildConfiguration}
          enablePipelines={enablePipelines}
          openEditor={openEditor}
          repository={repository}
        />

        <Drawer onClose={openDiscardDialog} isOpen={isEditorOpen} width="full">
          <Header>
            <h2>Create your first pipeline</h2>
          </Header>
          <ModalTransition>
            {isDiscardDialogOpen && (
              <ModalDialog onClose={closeDiscardDialog}>
                <ModalHeader>
                  <ModalTitle>Discard the changes?</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <p>
                    Navigating away from this page will lose all the
                    configuration and your changes will not be saved. Are you
                    sure you would like to continue?
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button appearance="primary" onClick={closeEditor}>
                    Yes
                  </Button>
                  <Button appearance="subtle" onClick={closeDiscardDialog}>
                    Cancel
                  </Button>
                </ModalFooter>
              </ModalDialog>
            )}
          </ModalTransition>
          <AnalyticsContext data={{ source }}>
            <EditorContainer>
              <EditorHeader>
                <strong>
                  {repository.name ? `${repository.name}/` : ''}
                  bitbucket-pipelines.yml
                </strong>
                <Lozenge appearance="inprogress">Editable</Lozenge>
              </EditorHeader>
              <EditorWrapper>
                <Editor />
              </EditorWrapper>
              <OnboardingCommit source={source} />
            </EditorContainer>
          </AnalyticsContext>
        </Drawer>
        {isRunPipelineModalOpen ? (
          <RunPipelineModal
            isPipelinesDisabled={!capabilities.pipelinesEnabled}
            isRepoReadOnly={!repository.canWrite}
            isOverAllowance={
              capabilities.limited &&
              capabilities.allowancePercentageUsed >= ALLOWANCE_LIMIT
            }
            fetchBranches={fetchBranches}
            fetchCreatePipeline={fetchCreatePipeline}
            openPipelinePage={openPipelinePage}
            onCloseDialog={closeRunPipelineModal}
            plansPageUrl={`/${workspaceSlug}/workspace/settings/plans-and-billing/`}
            settingsPageUrl={`/${repositoryFullSlug}/admin/pipelines/settings`}
            showBlanket
          />
        ) : null}
      </OnboardingWrapper>
    </ContextualAnalyticsData>
  );
};

export default React.memo(Onboarding);
